import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Collapse, Fade, Grow, Slide, Zoom } from "@mui/material";

interface IProps {
  children: JSX.Element;
  width?: "fit-content" | "100%";
  direction?: "left" | "right" | "up" | "down" | undefined;
  orientation?: "vertical" | "horizontal" | undefined;
}

const RevealWithSlide: React.FC<IProps> = ({ children, width = "100%" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const mainControls = useAnimation();
  const slideControls = useAnimation();

  useEffect(() => {
    if (isInView) {
      mainControls.start("visible");
      slideControls.start("visible");
    }
  }, [isInView]);

  return (
    <div ref={ref} style={{ position: "relative", width, overflow: "hidden" }}>
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 75 },
          visible: { opacity: 1, y: 0 },
        }}
        initial="hidden"
        animate={mainControls}
        transition={{ duration: 0.5, delay: 0.25 }}
      >
        {children}
      </motion.div>
      <motion.div
        variants={{
          hidden: { left: 0 },
          visible: { left: "100%" },
        }}
        initial="hidden"
        animate={slideControls}
        transition={{ duration: 0.5, delay: 1, ease: "easeInOut" }}
        style={{
          position: "absolute",
          top: 4,
          bottom: 4,
          left: 0,
          right: 0,
          // background:"#00274c20",
          zIndex: 20,
          borderRadius: "15px",
        }}
      />
    </div>
  );
};

export const Reveal: React.FC<IProps> = ({ children, width = "100%" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const mainControls = useAnimation();

  useEffect(() => {
    if (isInView) {
      mainControls.start("visible");
    }
  }, [isInView]);

  return (
    <div ref={ref} style={{ position: "relative", width, overflow: "hidden" }}>
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 75 },
          visible: { opacity: 1, y: 0 },
        }}
        initial="hidden"
        animate={mainControls}
        transition={{ duration: 0.5, delay: 0.25 }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export const RevealGrow: React.FC<IProps> = ({ children, width = "100%" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  return (
    <div ref={ref} style={{ position: "relative", width, overflow: "hidden" }}>
      <Grow
        in={isVisible}
        timeout={{ enter: 1500, exit: 500 }}
        style={{ transitionTimingFunction: "ease-in-out" }}
      >
        <div>{children}</div>
      </Grow>
    </div>
  );
};

export const RevealFade: React.FC<IProps> = ({ children }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  return (
    <div ref={ref} >
      <Fade
        in={isVisible}
        timeout={{ enter: 1000, exit: 500 }}
        style={{ transitionTimingFunction: "ease-in-out" }}
      >
        <div>{children}</div>
      </Fade>
    </div>
  );
};

export const RevealSlide: React.FC<IProps> = ({
  children,
  width = "100%",
  direction,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  return (
    <div ref={ref} style={{ position: "relative", width, overflow: "hidden" }}>
      <Slide
        direction={direction}
        in={isVisible}
        timeout={{ enter: 1200, exit: 500 }}
        easing={{ enter: "1200", exit: "500" }}
        style={{ transitionTimingFunction: "ease-in-out" }}
      >
        <div>{children}</div>
      </Slide>
    </div>
  );
};

export const RevealCollapse: React.FC<IProps> = ({
  children,
  width = "100%",
  orientation,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  return (
    <div ref={ref} style={{ position: "relative", width, overflow: "hidden" }}>
      <Collapse
        orientation={orientation}
        collapsedSize={0}
        easing={{ enter: "1500", exit: "500" }}
        in={isVisible}
        timeout={{ enter: 1500, exit: 500 }}
        style={{ transitionTimingFunction: "ease-in-out" }}
      >
        <div>{children}</div>
      </Collapse>
    </div>
  );
};

export const RevealZoom: React.FC<IProps> = ({ children, width = "100%" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  return (
    <div ref={ref} style={{ position: "relative", width, overflow: "hidden" }}>
      <Zoom
        easing={{ enter: "2000", exit: "500" }}
        in={isVisible}
        timeout={{ appear: 200, enter: 800, exit: 500 }}
        style={{ transitionTimingFunction: "ease-in-out" }}
      >
        <div>{children}</div>
      </Zoom>
    </div>
  );
};

export default RevealWithSlide;
